import {Box, Label, Link, Text} from '@primer/react'
import {userHovercardPath} from '@github-ui/paths'
import {LABELS} from '../../constants/labels'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {graphql} from 'relay-runtime'
import {useFragment} from 'react-relay'
import type {RepositoryPill$key} from './__generated__/RepositoryPill.graphql'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {FC} from 'react'

const RepositoryPillFragment = graphql`
  fragment RepositoryPill on Issue {
    repository {
      name
      owner {
        login
        avatarUrl(size: 64)
        ownerType: __typename
      }
      isPrivate
    }
  }
`

export type RepositoryPillProps = {
  repositoryPillData: RepositoryPill$key
  sx?: BetterSystemStyleObject
  isSmall?: boolean
}

export const SmallRepositoryPill: FC<RepositoryPillProps> = props => <RepositoryPill {...props} isSmall={true} />

export function RepositoryPill({repositoryPillData, sx, isSmall = false}: RepositoryPillProps) {
  const {repository} = useFragment(RepositoryPillFragment, repositoryPillData)
  const repoOwner = repository.owner.login
  const repoName = repository.name

  const link = (
    <Box
      sx={{
        alignItems: 'center',
        color: 'fg.subtle',
        display: 'flex',
        flexDirection: 'row',
        flexShrink: 1,
        flexWrap: 'nowrap',
        fontSize: 0,
        fontWeight: 'normal',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        height: isSmall ? undefined : '32px',
      }}
    >
      <Link
        href={`/${repoOwner}`}
        sx={{
          color: 'fg.muted',
          fontWeight: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexBasis: 'auto',
          flexShrink: 1,
        }}
        data-hovercard-url={userHovercardPath({owner: repoOwner})}
      >
        <GitHubAvatar
          square={repository.owner.ownerType === 'Organization'}
          src={repository.owner.avatarUrl}
          size={16}
          alt={`${repoOwner}`}
          sx={{mr: 1, mb: '1px', ml: '1px'}}
        />
        {repoOwner}
      </Link>
      <Text as="span" sx={{mb: '1px'}}>
        {'/'}
      </Text>
      <Link
        href={`/${repoOwner}/${repoName}`}
        sx={{
          color: 'fg.muted',
          fontWeight: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexBasis: 'auto',
          flexShrink: 4,
          mb: '1px',
        }}
      >
        {repoName}
      </Link>
    </Box>
  )

  if (isSmall) {
    return link
  }

  return (
    <Box
      sx={{
        color: 'fg.subtle',
        fontWeight: 'normal',
        fontSize: 0,
        display: 'flex',
        gap: 1,
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {link}
      <Label data-watch-overflow sx={{ml: 1, color: 'fg.default'}}>
        {repository.isPrivate ? LABELS.private : LABELS.public}
      </Label>
    </Box>
  )
}

try{ RepositoryPill.displayName ||= 'RepositoryPill' } catch {}